export const COOKIE_CONSENT_STORAGE_KEY = "hommerfitness.cookie-consent";
export const COOKIE_CONSENT_OPEN_EVENT = "hommerfitness:cookie-consent-open";

export type CookieConsentState = {
  necessary: true;
  analytics: boolean;
  answered: boolean;
  updatedAt: string | null;
  version: 1;
};

const defaultConsentState: CookieConsentState = {
  necessary: true,
  analytics: false,
  answered: false,
  updatedAt: null,
  version: 1,
};

function isBrowser() {
  return typeof window !== "undefined";
}

function normalizeConsentState(
  value: Partial<CookieConsentState> | null | undefined,
): CookieConsentState {
  return {
    necessary: true,
    analytics: value?.analytics === true,
    answered: value?.answered === true,
    updatedAt: typeof value?.updatedAt === "string" ? value.updatedAt : null,
    version: 1,
  };
}

export function getDefaultConsentState() {
  return defaultConsentState;
}

export function readCookieConsent(): CookieConsentState {
  if (!isBrowser()) {
    return defaultConsentState;
  }

  try {
    const storedValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);

    if (!storedValue) {
      return defaultConsentState;
    }

    return normalizeConsentState(JSON.parse(storedValue));
  } catch {
    return defaultConsentState;
  }
}

export function writeCookieConsent(
  nextState: Pick<CookieConsentState, "analytics" | "answered">,
) {
  const normalized = normalizeConsentState({
    analytics: nextState.analytics,
    answered: nextState.answered,
    updatedAt: new Date().toISOString(),
  });

  if (isBrowser()) {
    window.localStorage.setItem(
      COOKIE_CONSENT_STORAGE_KEY,
      JSON.stringify(normalized),
    );
  }

  return normalized;
}

export function acceptAnalyticsCookies() {
  return writeCookieConsent({ analytics: true, answered: true });
}

export function rejectAnalyticsCookies() {
  return writeCookieConsent({ analytics: false, answered: true });
}

export function openCookieConsentSettings() {
  if (!isBrowser()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_OPEN_EVENT));
}
